import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import CommonPageHeader from "../../common/ui/page-header/CommonPageHeader";
import { GridPager } from "../../common/pagination/GridPager";
import PayrollDataGrid, { payrollActionsCellClass, payrollActionsHeaderClass } from "./PayrollDataGrid";
import PayrollModal from "./PayrollModal";
import { axiosBasic, getUserInfo } from "../../../api/axios";
import { config } from "../../../api/endPoints";
import { Common } from "../../../utils";
import { getSelectedCompany } from "../../Global/CustomFunctions";
import "../attendance/Attendance.css";
import "../documents/Documents.css";
import "../timesheets/TimeSheets.css";
import "./Payroll.css";
import { useWorkplaceGate } from "../../../hooks/useWorkplaceGate";

const PAGE_SIZE = 10;
const { SalaryComponent } = config.REST_API;

/**
 * Filter tab keys — align with API `SalaryComponentKind` (short): 0..4
 * @typedef {'all' | 'earning' | 'deduction' | 'statutoryDeduction' | 'employerContribution' | 'other'} ComponentTab
 */

/** Matches C# `SalaryComponentKind` order */
const KIND = {
  Earning: 0,
  Deduction: 1,
  StatutoryDeduction: 2,
  EmployerContribution: 3,
  Other: 4,
};

/** @type {Record<ComponentTab, number>} */
const TAB_TO_KIND = {
  earning: KIND.Earning,
  deduction: KIND.Deduction,
  statutoryDeduction: KIND.StatutoryDeduction,
  employerContribution: KIND.EmployerContribution,
  other: KIND.Other,
};

/** @param {unknown} kind */
const normalizeKindNumber = (kind) => {
  if (typeof kind === "number" && Number.isFinite(kind)) {
    const n = Math.trunc(kind);
    if (n >= 0 && n <= 4) return n;
  }
  const raw = String(kind ?? "").trim();
  if (!raw) return KIND.Earning;
  if (/^-?\d+$/.test(raw)) {
    const n = parseInt(raw, 10);
    if (n >= 0 && n <= 4) return n;
  }
  const compact = raw.replace(/\s+/g, "").replace(/_/g, "").toLowerCase();
  if (compact === "earning") return KIND.Earning;
  if (compact === "deduction") return KIND.Deduction;
  if (compact === "statutorydeduction") return KIND.StatutoryDeduction;
  if (compact === "employercontribution") return KIND.EmployerContribution;
  if (compact === "other") return KIND.Other;
  const low = raw.toLowerCase();
  if (low.includes("statutory") && low.includes("deduct")) return KIND.StatutoryDeduction;
  if (low.includes("employer") && low.includes("contribution")) return KIND.EmployerContribution;
  if (low.includes("reimburse")) return KIND.Other;
  if (low.includes("other")) return KIND.Other;
  if (low.includes("deduct")) return KIND.Deduction;
  return KIND.Earning;
};

/**
 * Salary component list payloads may use `id`, `Id`, `SalaryComponentId`, etc.
 * An empty id yields `DELETE SalaryComponent/` which the API treats as null.
 * @param {Record<string, unknown>} r
 */
const resolveSalaryComponentIdFromRaw = (r) => {
  if (!r || typeof r !== "object") return "";
  const keys = ["id", "Id", "salaryComponentId", "SalaryComponentId", "componentId", "ComponentId"];
  for (const k of keys) {
    const v = r[k];
    if (v === undefined || v === null) continue;
    const s = String(v).trim();
    if (s && s !== "null" && s !== "undefined") return s;
  }
  return "";
};

/** @param {unknown} raw */
const normalizeSalaryComponent = (raw) => {
  const r = raw && typeof raw === "object" ? raw : {};
  const get = (a, b) => (r[a] !== undefined && r[a] !== null ? r[a] : r[b]);
  const officeIdRaw = get("officeId", "OfficeId");
  return {
    id: resolveSalaryComponentIdFromRaw(r),
    companyId: String(get("companyId", "CompanyId") ?? ""),
    officeId: officeIdRaw != null && officeIdRaw !== "" ? Number(officeIdRaw) : null,
    officeName: get("officeName", "OfficeName") ?? "",
    name: get("name", "Name") ?? "",
    code: get("code", "Code") ?? "",
    description: get("description", "Description") ?? "",
    kind: normalizeKindNumber(get("kind", "Kind")),
    isStatutory: Boolean(get("isStatutory", "IsStatutory")),
    sortOrder: Number(get("sortOrder", "SortOrder") ?? 0),
    createdDate: get("createdDate", "CreatedDate"),
    updatedDate: get("updatedDate", "UpdatedDate"),
    isActive: get("isActive", "IsActive") !== false,
    officeCountryName: get("officeCountryName", "OfficeCountryName") ?? "",
    officeCurrency: get("officeCurrency", "OfficeCurrency") ?? "",
    officeCurrencyName: get("officeCurrencyName", "OfficeCurrencyName") ?? "",
  };
};

/** @param {unknown} raw */
const normalizeOffice = (raw) => {
  const r = raw && typeof raw === "object" ? raw : {};
  const get = (a, b) => (r[a] !== undefined && r[a] !== null ? r[a] : r[b]);
  return {
    id: Number(get("id", "Id")),
    name: String(get("name", "Name") ?? ""),
    countryName: String(get("countryName", "CountryName") ?? ""),
    currency: String(get("currency", "Currency") ?? ""),
    currencyName: String(get("currencyName", "CurrencyName") ?? ""),
  };
};

/** @param {string} name */
const generateCodeFromName = (name) => {
  const s = name
    .trim()
    .replace(/[^a-zA-Z0-9]+/g, "_")
    .replace(/^_|_$/g, "")
    .toUpperCase();
  return (s || "COMP").slice(0, 50);
};

/**
 * @template T
 * @param {import('axios').AxiosResponse} res
 * @param {(msg?: string) => void} onFail
 * @returns {T[]}
 */
const unwrapArray = (res, onFail) => {
  const body = res?.data;
  if (body == null) return [];
  if (body.success === false) {
    onFail(body.message);
    return [];
  }
  if (Array.isArray(body)) return body;
  if (Array.isArray(body.data)) return body.data;
  return [];
};

/**
 * @param {import('axios').AxiosResponse} res
 * @param {(msg?: string) => void} onFail
 */
const unwrapSuccess = (res, onFail) => {
  const body = res?.data;
  if (body == null) return false;
  if (body.success === false) {
    onFail(body.message);
    return false;
  }
  return true;
};

/** @param {ReturnType<typeof normalizeSalaryComponent>} row */
const formatCurrencyCell = (row) => {
  if (!row.officeCurrency) return "—";
  if (row.officeCurrencyName) return `${row.officeCurrency} (${row.officeCurrencyName})`;
  return row.officeCurrency;
};

/** @param {string | null | undefined} description */
const renderDescriptionCell = (description) => {
  const desc = description && String(description).trim();
  if (!desc) return "—";
  return (
    <span className="payroll-sc-grid-desc" title={desc}>
      {desc}
    </span>
  );
};

/** @param {string | null | undefined} iso */
const formatShortDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
};

const SalaryComponentsPage = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { withWorkplace, workplaceRequiredModal } = useWorkplaceGate("salaryComponents");

  const [tab, setTab] = useState(/** @type {ComponentTab} */ ("all"));
  const [filterOfficeId, setFilterOfficeId] = useState("");
  /** @type {'all' | 'active' | 'inactive'} */
  const [filterStatus, setFilterStatus] = useState("all");
  const [page, setPage] = useState(1);

  const [rows, setRows] = useState(/** @type {ReturnType<typeof normalizeSalaryComponent>[]} */ ([]));
  const [offices, setOffices] = useState(/** @type {ReturnType<typeof normalizeOffice>[]} */ ([]));

  const [listLoading, setListLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState(/** @type {'create' | 'edit'} */ ("create"));
  const [editing, setEditing] = useState(/** @type {ReturnType<typeof normalizeSalaryComponent> | null} */ (null));
  const [formName, setFormName] = useState("");
  const [formKind, setFormKind] = useState(KIND.Earning);
  const [formOfficeId, setFormOfficeId] = useState("");
  const [formDescription, setFormDescription] = useState("");
  const [formIsStatutory, setFormIsStatutory] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toggleTarget, setToggleTarget] = useState(
    /** @type {{ id: string, name: string, toActive: boolean } | null} */ (null)
  );
  const [toggleLoading, setToggleLoading] = useState(false);

  const loadOffices = useCallback(async () => {
    try {
      const res = await axiosBasic.get(SalaryComponent.Offices);
      const list = unwrapArray(res, (msg) => Common.showErrorMessage(msg || t("app.payroll.salaryComponents.loadOfficesError")));
      setOffices(list.map(normalizeOffice).filter((o) => !Number.isNaN(o.id)));
    } catch {
      Common.showErrorMessage(t("app.payroll.salaryComponents.loadOfficesError"));
      setOffices([]);
    }
  }, [t]);

  const loadList = useCallback(async () => {
    setListLoading(true);
    try {
      const res = await axiosBasic.get(SalaryComponent.Get);
      const list = unwrapArray(res, (msg) => Common.showErrorMessage(msg || t("app.payroll.salaryComponents.loadListError")));
      setRows(list.map(normalizeSalaryComponent));
    } catch {
      Common.showErrorMessage(t("app.payroll.salaryComponents.loadListError"));
      setRows([]);
    } finally {
      setListLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadOffices();
    loadList();
  }, [loadList, loadOffices]);

  useEffect(() => {
    setPage(1);
  }, [tab, filterOfficeId, filterStatus]);

  /** Statutory deduction components are always statutory; checkbox follows type. */
  useEffect(() => {
    if (formKind === KIND.StatutoryDeduction) {
      setFormIsStatutory(true);
    }
  }, [formKind]);

  const tabKind = tab === "all" ? null : TAB_TO_KIND[tab];
  const isStatutoryKindLocked = formKind === KIND.StatutoryDeduction;

  const filtered = useMemo(() => {
    let list = tabKind == null ? rows : rows.filter((r) => r.kind === tabKind);
    if (filterOfficeId) {
      const oid = Number(filterOfficeId);
      list = list.filter((r) => r.officeId === oid);
    }
    if (filterStatus === "active") list = list.filter((r) => r.isActive);
    else if (filterStatus === "inactive") list = list.filter((r) => !r.isActive);
    return list;
  }, [rows, tabKind, filterOfficeId, filterStatus]);

  const total = filtered.length;
  const slice = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page]);

  const activeCount = useMemo(() => rows.filter((r) => r.isActive).length, [rows]);
  const inCategoryCount = useMemo(() => {
    if (tab === "all") return filtered.filter((r) => r.isActive).length;
    if (tabKind == null) return 0;
    return rows.filter((r) => r.isActive && r.kind === tabKind).length;
  }, [tab, tabKind, rows, filtered]);
  const lastUpdatedLabel = useMemo(() => {
    let best = null;
    for (const r of rows) {
      const u = r.updatedDate;
      if (!u) continue;
      const tms = new Date(u).getTime();
      if (Number.isNaN(tms)) continue;
      if (best == null || tms > best.tms) best = { tms, u };
    }
    return best ? formatShortDate(best.u) : "—";
  }, [rows]);

  const typeLabel = (kind) => {
    if (kind === KIND.Deduction) return t("app.payroll.salaryComponents.typeDeduction");
    if (kind === KIND.StatutoryDeduction) return t("app.payroll.salaryComponents.typeStatutoryDeduction");
    if (kind === KIND.EmployerContribution) return t("app.payroll.salaryComponents.typeEmployerContribution");
    if (kind === KIND.Other) return t("app.payroll.salaryComponents.typeOther");
    return t("app.payroll.salaryComponents.typeEarning");
  };

  const kindBadgeClass = (kind) => {
    if (kind === KIND.Deduction) return "payroll-badge--deduction";
    if (kind === KIND.StatutoryDeduction) return "payroll-badge--statutory-deduction";
    if (kind === KIND.EmployerContribution) return "payroll-badge--employer-contribution";
    if (kind === KIND.Other) return "payroll-badge--other-kind";
    return "payroll-badge--earning";
  };

  const selectedOfficeForForm = useMemo(() => {
    if (!formOfficeId) return null;
    const id = Number(formOfficeId);
    return offices.find((o) => o.id === id) ?? null;
  }, [formOfficeId, offices]);

  const modalOfficeOptions = useMemo(() => {
    const base = [...offices];
    if (modalMode === "edit" && editing?.officeId != null) {
      const exists = base.some((o) => o.id === editing.officeId);
      if (!exists && editing.officeName) {
        base.unshift({
          id: editing.officeId,
          name: editing.officeName,
          countryName: editing.officeCountryName || "",
          currency: editing.officeCurrency || "",
          currencyName: editing.officeCurrencyName || "",
        });
      }
    }
    return base;
  }, [offices, modalMode, editing]);

  const openCreate = () => {
    if (!withWorkplace()) return;
    setModalMode("create");
    setEditing(null);
    setFormName("");
    setFormKind(tab === "all" ? KIND.Earning : tabKind);
    setFormOfficeId("");
    setFormDescription("");
    setFormIsStatutory(false);
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setModalMode("edit");
    setEditing(row);
    setFormName(row.name);
    setFormKind(row.kind);
    setFormOfficeId(row.officeId != null ? String(row.officeId) : "");
    setFormDescription(row.description ?? "");
    setFormIsStatutory(Boolean(row.isStatutory));
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;
    setModalOpen(false);
    setEditing(null);
  };

  const persist = async () => {
    const name = formName.trim();
    if (!name) {
      Common.showErrorMessage(t("app.payroll.salaryComponents.fieldNamePh"));
      return;
    }
    const selected = getSelectedCompany();
    const fromCompany =
      selected && typeof selected === "object"
        ? selected.id ?? selected.Id ?? selected.companyId ?? selected.CompanyId
        : "";
    const user = getUserInfo();
    const companyId = String(fromCompany ?? user?.companyId ?? user?.CompanyId ?? "");
    if (!companyId) {
      Common.showErrorMessage(t("app.payroll.salaryComponents.companyMissing"));
      return;
    }

    const officeId = formOfficeId ? Number(formOfficeId) : null;

    setSaving(true);
    try {
      if (modalMode === "create") {
        const body = {
          id: "",
          companyId,
          officeId,
          name,
          code: generateCodeFromName(name),
          description: formDescription.trim().slice(0, 2000),
          kind: formKind,
          isStatutory: isStatutoryKindLocked ? true : formIsStatutory,
          sortOrder: 0,
          isActive: true,
        };
        const res = await axiosBasic.post(SalaryComponent.Post, body);
        if (!unwrapSuccess(res, (msg) => Common.showErrorMessage(msg || t("app.payroll.salaryComponents.saveError")))) {
          return;
        }
        Common.showSuccessMessage(t("app.payroll.common.save"));
        setModalOpen(false);
        await loadList();
        return;
      }

      if (!editing) return;
      const body = {
        ...editing,
        companyId: editing.companyId || companyId,
        name,
        kind: formKind,
        officeId,
        code: editing.code || generateCodeFromName(name),
        description: formDescription.trim().slice(0, 2000),
        isStatutory: isStatutoryKindLocked ? true : formIsStatutory,
      };
      const res = await axiosBasic.put(SalaryComponent.Put, body);
      if (!unwrapSuccess(res, (msg) => Common.showErrorMessage(msg || t("app.payroll.salaryComponents.saveError")))) {
        return;
      }
      Common.showSuccessMessage(t("app.payroll.common.save"));
      setModalOpen(false);
      setEditing(null);
      await loadList();
    } catch {
      Common.showErrorMessage(t("app.payroll.salaryComponents.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const openToggleActiveConfirm = (row) => {
    const id = String(row?.id ?? "").trim();
    if (!id || id === "null" || id === "undefined") {
      Common.showErrorMessage(t("app.payroll.salaryComponents.deactivateMissingId"));
      return;
    }
    setToggleTarget({
      id,
      name: (row.name && String(row.name).trim()) || "—",
      toActive: !row.isActive,
    });
  };

  const closeToggleActiveConfirm = () => {
    if (toggleLoading) return;
    setToggleTarget(null);
  };

  const handleConfirmToggleActive = async () => {
    const id = String(toggleTarget?.id ?? "").trim();
    if (!id || id === "null" || id === "undefined") {
      Common.showErrorMessage(t("app.payroll.salaryComponents.deactivateMissingId"));
      return;
    }
    const toActive = Boolean(toggleTarget?.toActive);
    setToggleLoading(true);
    try {
      const url = `${SalaryComponent.ToggleActiveBase}${encodeURIComponent(id)}/toggle-active`;
      const res = await axiosBasic.put(url, {});
      const errKey = toActive ? "app.payroll.salaryComponents.activateError" : "app.payroll.salaryComponents.deactivateError";
      if (!unwrapSuccess(res, (msg) => Common.showErrorMessage(msg || t(errKey)))) {
        return;
      }
      Common.showSuccessMessage(
        toActive ? t("app.payroll.salaryComponents.activateSuccess") : t("app.payroll.salaryComponents.deactivateSuccess")
      );
      setToggleTarget(null);
      await loadList();
    } catch {
      Common.showErrorMessage(
        toActive ? t("app.payroll.salaryComponents.activateError") : t("app.payroll.salaryComponents.deactivateError")
      );
    } finally {
      setToggleLoading(false);
    }
  };

  const goCompliance = () => {
    history.push("/documents/compliance");
  };

  const columns = useMemo(
    () => [
      {
        key: "name",
        label: t("app.payroll.salaryComponents.colName"),
        sortable: false,
        headerClassName: "payroll-sc-grid-th payroll-sc-grid-th--name",
      },
      {
        key: "description",
        label: t("app.payroll.salaryComponents.colDescription"),
        sortable: false,
        headerClassName: "payroll-sc-grid-th payroll-sc-grid-th--description",
      },
      {
        key: "type",
        label: t("app.payroll.salaryComponents.colType"),
        sortable: false,
        headerClassName: "payroll-sc-grid-th payroll-sc-grid-th--type",
      },
      {
        key: "office",
        label: t("app.payroll.salaryComponents.colOffice"),
        sortable: false,
        headerClassName: "payroll-sc-grid-th payroll-sc-grid-th--office",
      },
      {
        key: "currency",
        label: t("app.payroll.salaryComponents.colCurrency"),
        sortable: false,
        headerClassName: "payroll-sc-grid-th payroll-sc-grid-th--currency",
      },
      {
        key: "status",
        label: t("app.payroll.salaryComponents.colStatus"),
        sortable: false,
        headerClassName: "payroll-sc-grid-th payroll-sc-grid-th--status",
      },
      {
        key: "actions",
        label: t("app.payroll.common.actions"),
        sortable: false,
        headerClassName: [payrollActionsHeaderClass, "payroll-sc-grid-th payroll-sc-grid-th--actions"].join(" "),
      },
    ],
    [t]
  );

  const currencyReadout = selectedOfficeForForm
    ? selectedOfficeForForm.currency
      ? selectedOfficeForForm.currencyName
        ? `${selectedOfficeForForm.currency} (${selectedOfficeForForm.currencyName})`
        : selectedOfficeForForm.currency
      : "—"
    : "—";

  return (
    <div className="documents-page payroll-page ts-page px-3 px-md-4 py-4">
      <CommonPageHeader
        icon={<i className="bi bi-ui-checks-grid" />}
        title={t("app.payroll.salaryComponents.title")}
        description={t("app.payroll.salaryComponents.subtitle")}
      />

      <section className="payroll-sc-stats-wrap mb-0" aria-label={t("app.payroll.common.statsAria")}>
        <div className="ts-appr-stats payroll-sc-summary-stats">
          <div className="ts-appr-stat-card ts-appr-stat-card--approved">
            <div className="ts-appr-stat-card__accent" aria-hidden />
            <div className="ts-appr-stat-card__inner">
              <div className="ts-appr-stat-card__top">
                <span className="ts-appr-stat-card__icon">
                  <i className="bi bi-check2-circle" />
                </span>
                <div className="ts-appr-stat-card__figures">
                  <span className="ts-appr-stat-card__value">{String(activeCount).padStart(2, "0")}</span>
                  <span className="ts-appr-stat-card__label">{t("app.payroll.salaryComponents.statActive")}</span>
                </div>
              </div>
            </div>
          </div>
          <div className="ts-appr-stat-card ts-appr-stat-card--awaiting">
            <div className="ts-appr-stat-card__accent" aria-hidden />
            <div className="ts-appr-stat-card__inner">
              <div className="ts-appr-stat-card__top">
                <span className="ts-appr-stat-card__icon">
                  <i className="bi bi-funnel" />
                </span>
                <div className="ts-appr-stat-card__figures">
                  <span className="ts-appr-stat-card__value">{String(inCategoryCount).padStart(2, "0")}</span>
                  <span className="ts-appr-stat-card__label">{t("app.payroll.salaryComponents.statInCategory")}</span>
                </div>
              </div>
            </div>
          </div>
          <div className="ts-appr-stat-card ts-appr-stat-card--total">
            <div className="ts-appr-stat-card__accent" aria-hidden />
            <div className="ts-appr-stat-card__inner">
              <div className="ts-appr-stat-card__top">
                <span className="ts-appr-stat-card__icon">
                  <i className="bi bi-clock-history" />
                </span>
                <div className="ts-appr-stat-card__figures">
                  <span className="ts-appr-stat-card__value payroll-sc-stat-date-value">{lastUpdatedLabel}</span>
                  <span className="ts-appr-stat-card__label">{t("app.payroll.salaryComponents.statLastUpdated")}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="payroll-sc-toolbar-card mb-3" aria-label={t("app.payroll.salaryComponents.filterToolbarLabel")}>
        <div className="payroll-sc-toolbar-card__head">
          <div>
            <h2 className="payroll-sc-toolbar-card__title">{t("app.payroll.salaryComponents.filterToolbarLabel")}</h2>
            <p className="payroll-sc-toolbar-card__subtitle text-muted mb-0">{t("app.payroll.salaryComponents.filterHint")}</p>
          </div>
          <div className="payroll-sc-toolbar-card__actions payroll-sc-toolbar-card__actions--equal-pair">
            <button type="button" className="btn btn-outline-secondary" onClick={goCompliance}>
              <i className="bi bi-patch-check me-1" aria-hidden />
              {t("app.payroll.salaryComponents.complianceAudit")}
            </button>
            <button type="button" className="btn btn-primary" onClick={openCreate}>
              <i className="bi bi-plus-lg me-1" aria-hidden />
              {t("app.payroll.salaryComponents.new")}
            </button>
          </div>
        </div>
        <div className="payroll-sc-toolbar-card__body">
          <div>
            <span className="payroll-sc-toolbar-card__row-label">{t("app.payroll.salaryComponents.filterTypeLabel")}</span>
            <div
              className="ts-view-tabs ts-view-tabs--timelog-inline payroll-sc-kind-tabs"
              role="tablist"
              aria-label={t("app.payroll.salaryComponents.title")}
            >
              {(
                /** @type {const} */ ([
                  ["all", "tabAll"],
                  ["earning", "tabEarnings"],
                  ["deduction", "tabDeductions"],
                  ["statutoryDeduction", "tabStatutoryDeductions"],
                  ["employerContribution", "tabEmployerContributions"],
                  ["other", "tabOther"],
                ])
              ).map(([key, i18nKey]) => (
                <button
                  key={key}
                  type="button"
                  role="tab"
                  aria-selected={tab === key}
                  className={tab === key ? "is-active" : ""}
                  onClick={() => setTab(/** @type {ComponentTab} */ (key))}
                >
                  {t(`app.payroll.salaryComponents.${i18nKey}`)}
                </button>
              ))}
            </div>
          </div>
          <div className="payroll-sc-toolbar-filters-row">
            <div className="payroll-sc-filter-field">
              <label className="ts-tl-filter-bar-label text-muted" htmlFor="payroll-sc-filter-office">
                {t("app.payroll.salaryComponents.colOffice")}
              </label>
              <select
                id="payroll-sc-filter-office"
                className="form-select"
                value={filterOfficeId}
                onChange={(e) => setFilterOfficeId(e.target.value)}
              >
                <option value="">{t("app.payroll.common.allOffices")}</option>
                {offices.map((o) => (
                  <option key={o.id} value={String(o.id)}>
                    {o.countryName ? `${o.name} — ${o.countryName}` : o.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="payroll-sc-filter-field">
              <label className="ts-tl-filter-bar-label text-muted" htmlFor="payroll-sc-filter-status">
                {t("app.payroll.salaryComponents.filterStatusLabel")}
              </label>
              <select
                id="payroll-sc-filter-status"
                className="form-select"
                value={filterStatus}
                onChange={(e) => setFilterStatus(/** @type {'all' | 'active' | 'inactive'} */ (e.target.value))}
              >
                <option value="all">{t("app.payroll.salaryComponents.filterStatusAll")}</option>
                <option value="active">{t("app.payroll.common.statusActive")}</option>
                <option value="inactive">{t("app.payroll.common.statusInactive")}</option>
              </select>
            </div>
          </div>
        </div>
      </section>

      <div className="payroll-sc-panel payroll-sc-components-grid card border-0 shadow-sm">
        <div className="card-body p-3">
          <div className="payroll-sc-data-card__head">
            <h3 className="payroll-sc-data-card__title">{t("app.payroll.salaryComponents.tableListTitle")}</h3>
          </div>
          <PayrollDataGrid
            columns={columns}
            rows={slice}
            getRowKey={(row) => row.id}
            emptyMessage={t("app.payroll.common.noResults")}
            loading={listLoading}
            loadingMessage={t("app.common.loading")}
            onRowClick={(row) => openEdit(row)}
            getRowClassName={(row) => (modalOpen && editing?.id === row.id ? "payroll-sc-row-selected" : "")}
            renderRow={(row) => (
              <>
                <td className="payroll-sc-grid-td payroll-sc-grid-td--name fw-semibold">{row.name}</td>
                <td className="payroll-sc-grid-td payroll-sc-grid-td--description text-muted">
                  {renderDescriptionCell(row.description)}
                </td>
                <td className="payroll-sc-grid-td payroll-sc-grid-td--type">
                  <span className={`payroll-badge ${kindBadgeClass(row.kind)}`}>
                    {typeLabel(row.kind)}
                  </span>
                </td>
                <td className="payroll-sc-grid-td payroll-sc-grid-td--office">{row.officeName || "—"}</td>
                <td className="payroll-sc-grid-td payroll-sc-grid-td--currency text-muted">{formatCurrencyCell(row)}</td>
                <td className="payroll-sc-grid-td payroll-sc-grid-td--status">
                  {row.isActive ? (
                    <span className="payroll-badge payroll-badge--active">{t("app.payroll.common.statusActive")}</span>
                  ) : (
                    <span className="payroll-badge payroll-badge--draft">{t("app.payroll.common.statusInactive")}</span>
                  )}
                </td>
                <td
                  className={`${payrollActionsCellClass} payroll-sc-grid-td payroll-sc-grid-td--actions`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="documents-actions-inline payroll-sc-actions-inline">
                    <button
                      type="button"
                      className="documents-grid-action-btn documents-grid-action-btn--edit documents-action-compact"
                      onClick={(e) => {
                        e.stopPropagation();
                        openEdit(row);
                      }}
                    >
                      {t("app.payroll.common.edit")}
                    </button>
                    {row.isActive ? (
                      <button
                        type="button"
                        className="documents-grid-action-btn documents-grid-action-btn--delete documents-action-compact"
                        onClick={(e) => {
                          e.stopPropagation();
                          openToggleActiveConfirm(row);
                        }}
                      >
                        {t("app.payroll.common.deactivate")}
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="documents-grid-action-btn documents-grid-action-btn--view documents-action-compact"
                        onClick={(e) => {
                          e.stopPropagation();
                          openToggleActiveConfirm(row);
                        }}
                      >
                        {t("app.payroll.common.activate")}
                      </button>
                    )}
                  </div>
                </td>
              </>
            )}
          />

          <div className="documents-pagination documents-pagination--grid-pager d-flex justify-content-between align-items-center flex-wrap gap-2 mt-2 pt-1">
            <span className="documents-pagination-total text-muted small">
              {total === 0
                ? t("app.payroll.common.noResults")
                : t("app.payroll.common.showingRange", {
                    from: (page - 1) * PAGE_SIZE + 1,
                    to: Math.min(page * PAGE_SIZE, total),
                    total,
                  })}
            </span>
            <GridPager
              className="documents-rc-pagination"
              onChange={(p) => setPage(p)}
              current={page}
              total={total}
              pageSize={PAGE_SIZE}
              showLessItems
              showTitle={false}
              showSizeChanger={false}
            />
          </div>
        </div>
      </div>

      <p className="small text-muted mt-4 mb-0">{t("app.payroll.salaryComponents.workflowHint")}</p>

      <PayrollModal
        open={modalOpen}
        onClose={closeModal}
        size="medium"
        variant="salary"
        headerIcon={<i className="bi bi-ui-checks-grid" />}
        titleId="payroll-sc-modal-title"
        title={modalMode === "create" ? t("app.payroll.salaryComponents.modalNewTitle") : t("app.payroll.salaryComponents.modalEditTitle")}
        subtitle={
          modalMode === "create"
            ? t("app.payroll.salaryComponents.modalNewSubtitle")
            : formName.trim() || t("app.payroll.salaryComponents.fieldNamePh")
        }
        footer={
          <>
            <button type="button" className="att-btn att-btn--ghost" onClick={closeModal} disabled={saving}>
              {t("app.payroll.common.cancel")}
            </button>
            <button type="button" className="att-btn att-btn--save-primary" onClick={persist} disabled={saving}>
              {saving ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden />
                  {t("app.payroll.common.save")}
                </>
              ) : modalMode === "create" ? (
                t("app.payroll.salaryComponents.createComponent")
              ) : (
                t("app.payroll.salaryComponents.updateComponent")
              )}
            </button>
          </>
        }
      >
        <div className="payroll-sc-modal-form payroll-sc-modal-form--salary">
          <div>
            <p className="payroll-sc-modal-form__section-title">{t("app.payroll.salaryComponents.modalSectionBasics")}</p>
            <div className="row g-2">
              <div className="col-12">
                <label className="att-modal-field-label" htmlFor="payroll-sc-modal-name">
                  {t("app.payroll.salaryComponents.fieldName")}
                </label>
                <input
                  id="payroll-sc-modal-name"
                  className="form-control"
                  placeholder={t("app.payroll.salaryComponents.fieldNamePh")}
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  autoComplete="off"
                />
              </div>
              <div className="col-12">
                <label className="att-modal-field-label" htmlFor="payroll-sc-modal-kind">
                  {t("app.payroll.salaryComponents.fieldType")}
                </label>
                <select
                  id="payroll-sc-modal-kind"
                  className="form-select"
                  value={formKind}
                  onChange={(e) => setFormKind(Number(e.target.value))}
                >
                  <option value={KIND.Earning}>{t("app.payroll.salaryComponents.typeEarning")}</option>
                  <option value={KIND.Deduction}>{t("app.payroll.salaryComponents.typeDeduction")}</option>
                  <option value={KIND.StatutoryDeduction}>{t("app.payroll.salaryComponents.typeStatutoryDeduction")}</option>
                  <option value={KIND.EmployerContribution}>{t("app.payroll.salaryComponents.typeEmployerContribution")}</option>
                  <option value={KIND.Other}>{t("app.payroll.salaryComponents.typeOther")}</option>
                </select>
              </div>
            </div>
          </div>

          <hr className="payroll-sc-modal-form__divider" />

          <div>
            <p className="payroll-sc-modal-form__section-title">{t("app.payroll.salaryComponents.modalSectionWorkplace")}</p>
            <div className="row g-2">
              <div className="col-12">
                <label className="att-modal-field-label" htmlFor="payroll-sc-modal-office">
                  {t("app.payroll.salaryComponents.fieldOffice")}
                </label>
                <select
                  id="payroll-sc-modal-office"
                  className="form-select"
                  value={formOfficeId}
                  onChange={(e) => setFormOfficeId(e.target.value)}
                >
                  <option value="">{t("app.payroll.common.selectOffice")}</option>
                  {modalOfficeOptions.map((o) => (
                    <option key={o.id} value={String(o.id)}>
                      {o.countryName ? `${o.name} — ${o.countryName}` : o.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-12">
                <label className="att-modal-field-label" htmlFor="payroll-sc-modal-currency-readonly">
                  {t("app.payroll.salaryComponents.fieldCurrency")}
                </label>
                <input
                  id="payroll-sc-modal-currency-readonly"
                  className="form-control bg-light text-muted"
                  readOnly
                  value={currencyReadout}
                  aria-describedby="payroll-sc-currency-hint"
                />
                <p className="small text-muted mt-1 mb-0" id="payroll-sc-currency-hint">
                  {t("app.payroll.salaryComponents.currencyFromOffice")}
                </p>
              </div>
            </div>
          </div>

          <hr className="payroll-sc-modal-form__divider" />

          <div>
            <p className="payroll-sc-modal-form__section-title">{t("app.payroll.salaryComponents.modalSectionDetails")}</p>
            <label className="att-modal-field-label" htmlFor="payroll-sc-modal-description">
              {t("app.payroll.salaryComponents.fieldDescription")}
            </label>
            <textarea
              id="payroll-sc-modal-description"
              className="form-control payroll-sc-modal-textarea"
              rows={3}
              maxLength={2000}
              placeholder={t("app.payroll.salaryComponents.fieldDescriptionPh")}
              value={formDescription}
              onChange={(e) => setFormDescription(e.target.value)}
            />
            <p className="small text-muted mt-1 mb-0 text-end">
              {formDescription.length}/2000
            </p>
          </div>

          <label
            className={`payroll-sc-statutory${isStatutoryKindLocked ? " payroll-sc-statutory--locked" : ""}`}
            htmlFor="payroll-sc-modal-statutory"
          >
            <input
              type="checkbox"
              className="payroll-sc-statutory__input"
              id="payroll-sc-modal-statutory"
              checked={formIsStatutory}
              disabled={isStatutoryKindLocked}
              onChange={(e) => setFormIsStatutory(e.target.checked)}
            />
            <span className="payroll-sc-statutory__copy">
              <span className="payroll-sc-statutory__title">{t("app.payroll.salaryComponents.isStatutory")}</span>
              <span className="payroll-sc-statutory__hint">{t("app.payroll.salaryComponents.isStatutoryHint")}</span>
            </span>
          </label>
        </div>
      </PayrollModal>

      {toggleTarget && (
        <div
          className="documents-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="payroll-sc-toggle-active-dialog-title"
          onClick={(e) => {
            if (e.target === e.currentTarget && !toggleLoading) closeToggleActiveConfirm();
          }}
        >
          <div
            className="documents-modal documents-modal--upload-single documents-modal--delete-confirm"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="documents-modal-preview" style={{ borderRight: "none", background: "#fff" }}>
              <div className="documents-upload-modal-header">
                <div>
                  <p className="documents-upload-modal-eyebrow">{t("app.payroll.salaryComponents.deactivateDialogEyebrow")}</p>
                  <h2 className="documents-upload-modal-title" id="payroll-sc-toggle-active-dialog-title">
                    {toggleTarget.toActive
                      ? t("app.payroll.salaryComponents.activateDialogTitle")
                      : t("app.payroll.salaryComponents.deactivateDialogTitle")}
                  </h2>
                </div>
                <button
                  type="button"
                  className="btn btn-light documents-btn"
                  aria-label={t("app.common.close")}
                  disabled={toggleLoading}
                  onClick={closeToggleActiveConfirm}
                >
                  {t("app.common.close")}
                </button>
              </div>

              <div className="documents-modal-sheet documents-modal-sheet--upload documents-delete-confirm-body">
                <div className="documents-delete-confirm-summary">
                  <div className="documents-delete-confirm-icon-wrap" aria-hidden>
                    {toggleTarget.toActive ? <i className="bi bi-check-circle" /> : <i className="bi bi-slash-circle" />}
                  </div>
                  <p className="documents-delete-confirm-lead">
                    {toggleTarget.toActive
                      ? t("app.payroll.salaryComponents.activateDialogLead")
                      : t("app.payroll.salaryComponents.deactivateDialogLead")}
                  </p>
                </div>

                <div className="documents-delete-confirm-target">
                  <p className="documents-form-label documents-delete-confirm-target-label">
                    {t("app.payroll.salaryComponents.deactivateDialogTargetLabel")}
                  </p>
                  <p className="documents-delete-confirm-target-name">{toggleTarget.name}</p>
                </div>

                <div className="documents-modal-actions documents-delete-confirm-actions">
                  <button
                    type="button"
                    className="btn btn-light documents-btn"
                    disabled={toggleLoading}
                    onClick={closeToggleActiveConfirm}
                  >
                    {t("app.payroll.common.cancel")}
                  </button>
                  <button
                    type="button"
                    className={
                      toggleTarget.toActive
                        ? "btn documents-btn btn-success"
                        : "btn documents-btn documents-delete-confirm-btn"
                    }
                    disabled={toggleLoading}
                    onClick={handleConfirmToggleActive}
                  >
                    {toggleLoading
                      ? toggleTarget.toActive
                        ? t("app.payroll.salaryComponents.activateDialogActivating")
                        : t("app.payroll.salaryComponents.deactivateDialogDeactivating")
                      : toggleTarget.toActive
                        ? t("app.payroll.salaryComponents.activateDialogConfirm")
                        : t("app.payroll.salaryComponents.deactivateDialogConfirm")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {workplaceRequiredModal}
    </div>
  );
};

export default SalaryComponentsPage;
